import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Download, RefreshCcw } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { formatDate } from '../../lib/admin/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContestTab = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [artists, setArtists] = useState(0);
  const [supporters, setSupporters] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | artist | supporter

  const authHeaders = () => ({
    Authorization: `Bearer ${sessionStorage.getItem('adminToken')}`,
  });

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === 'all' ? '' : `?role=${filter}`;
      const { data } = await axios.get(`${API}/contest/interest${q}`, { headers: authHeaders() });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setArtists(data.artists || 0);
      setSupporters(data.supporters || 0);
    } catch (err) {
      toast({
        title: 'Failed to load contest signups',
        description: err.response?.data?.detail || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const downloadCsv = async () => {
    try {
      const resp = await axios.get(`${API}/contest/interest.csv`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'woty-interest.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Export failed', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-contest-tab">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="grid grid-cols-3 gap-3 sm:gap-4 w-full sm:w-auto">
          <Stat label="Total on list" value={total} />
          <Stat label="Artists" value={artists} accent />
          <Stat label="Supporters" value={supporters} />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetch} disabled={loading} data-testid="contest-refresh">
            <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button size="sm" onClick={downloadCsv} data-testid="contest-export">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="flex gap-2 text-sm">
        {[
          { v: 'all', label: 'All' },
          { v: 'artist', label: 'Artists only' },
          { v: 'supporter', label: 'Supporters only' },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            className={`px-3 py-1.5 rounded-full border transition ${
              filter === f.v
                ? 'bg-[#722F37] text-white border-[#722F37]'
                : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
            }`}
            data-testid={`contest-filter-${f.v}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-left">
              <Th>Signed up</Th>
              <Th>Role</Th>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>City</Th>
              <Th>Instagram</Th>
              <Th>Marketing</Th>
              <Th>Source</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center py-8 text-gray-400">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-gray-400">
                No signups yet. Share the campaign to get local artists on the list.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-b last:border-b-0 hover:bg-gray-50">
                <Td>{r.createdAt ? formatDate(r.createdAt) : ''}</Td>
                <Td>
                  <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${
                    r.role === 'artist' ? 'bg-[#722F37] text-white' : 'bg-gray-200 text-gray-700'
                  }`}>{r.role}</span>
                </Td>
                <Td>{r.firstName} {r.lastName || ''}</Td>
                <Td className="font-mono text-xs">{r.email}</Td>
                <Td>{r.city || '—'}</Td>
                <Td>{r.instagram || '—'}</Td>
                <Td>{r.marketingConsent ? '✓' : '—'}</Td>
                <Td>{r.utmSource || r.referralSource || '—'}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className={`rounded-lg border px-3 py-2 ${accent ? 'bg-[#FFFBEA] border-[#FDB913]/40' : 'bg-white'}`}>
    <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    <div className="text-2xl font-bold text-gray-900">{value}</div>
  </div>
);

const Th = ({ children }) => (
  <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">{children}</th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-2.5 ${className}`}>{children}</td>
);

export default ContestTab;
